import { useState, useMemo } from 'react';
import {
  Link2Off,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  FileText,
  Globe,
  Filter,
} from 'lucide-react';
import type { BrokenLink, PageResult } from '../../types';
import { UnverifiedLinksSection } from './UnverifiedLinksSection';

interface BrokenLinksProps {
  pageResults: PageResult[];
  loading?: boolean;
  selectedPageUrl?: string | null;
  reviewedLinks?: string[];
  onToggleLinkReviewed?: (targetUrls: string[], reviewed: boolean) => void;
}

type GroupBy = 'status' | 'source' | 'none';

const STATUS_CONFIG: Record<number, { label: string; color: string; bgColor: string }> = {
  404: { label: 'Not Found', color: 'text-red-700', bgColor: 'bg-red-100' },
  403: { label: 'Forbidden', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  500: { label: 'Server Error', color: 'text-red-700', bgColor: 'bg-red-100' },
  502: { label: 'Bad Gateway', color: 'text-red-700', bgColor: 'bg-red-100' },
  503: { label: 'Service Unavailable', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  504: { label: 'Gateway Timeout', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  0: { label: 'Connection Failed', color: 'text-red-700', bgColor: 'bg-red-100' },
};

function getStatusConfig(statusCode: number) {
  return STATUS_CONFIG[statusCode] || { label: `Error ${statusCode}`, color: 'text-red-700', bgColor: 'bg-red-100' };
}

function truncateUrl(url: string, maxLength = 60): string {
  if (url.length <= maxLength) return url;
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname + urlObj.search;
    if (path.length <= maxLength) return path;
    return '...' + path.slice(-maxLength);
  } catch {
    return '...' + url.slice(-maxLength);
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="p-1 hover:bg-gray-200 rounded"
      aria-label={copied ? 'Copied!' : 'Copy URL'}
    >
      {copied ? (
        <Check size={14} className="text-status-success" />
      ) : (
        <Copy size={14} className="text-gray-400" />
      )}
    </button>
  );
}

interface BrokenLinkCardProps {
  link: BrokenLink;
  showSource?: boolean;
}

function BrokenLinkCard({ link, showSource = true }: BrokenLinkCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const statusConfig = getStatusConfig(link.statusCode);

  return (
    <div className="border border-gray-light rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-4 p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex-1 min-w-0">
          {/* Status and Link Type */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded text-caption font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
              {link.statusCode} {statusConfig.label}
            </span>
            {link.isExternal ? (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-caption bg-blue-100 text-blue-700">
                <Globe size={12} />
                External
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-caption bg-gray-100 text-gray-600">
                <FileText size={12} />
                Internal
              </span>
            )}
          </div>

          {/* Target URL */}
          <div className="flex items-center gap-2">
            <Link2Off size={16} className="text-status-error flex-shrink-0" />
            <span className="text-body font-medium text-dark-text truncate" title={link.targetUrl}>
              {truncateUrl(link.targetUrl)}
            </span>
          </div>

          {/* Link Text */}
          {link.linkText && (
            <p className="text-body-sm text-gray-medium mt-1 line-clamp-1">
              Link text: "{link.linkText}"
            </p>
          )}

          {/* Source (if shown in non-grouped view) */}
          {showSource && (
            <p className="text-caption text-gray-medium mt-2">
              Found on: {truncateUrl(link.sourceUrl, 50)}
            </p>
          )}
        </div>

        {isExpanded ? (
          <ChevronDown size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-gray-light p-4 bg-gray-light/20 space-y-3">
          {/* Full Target URL */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Target URL</span>
            <div className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
              <a
                href={link.targetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-coral hover:underline truncate flex-1"
              >
                {link.targetUrl}
              </a>
              <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
              <CopyButton text={link.targetUrl} />
            </div>
          </div>

          {/* Source URL */}
          <div>
            <span className="text-caption text-gray-medium block mb-1">Source Page</span>
            <div className="flex items-center gap-2 bg-white p-2 rounded border border-gray-200">
              <a
                href={link.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-coral hover:underline truncate flex-1"
              >
                {link.sourceUrl}
              </a>
              <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
              <CopyButton text={link.sourceUrl} />
            </div>
          </div>

          {/* Link Text */}
          {link.linkText && (
            <div>
              <span className="text-caption text-gray-medium block mb-1">Link Text</span>
              <p className="text-body-sm bg-white p-2 rounded border border-gray-200">
                {link.linkText || <span className="text-gray-400 italic">No text (image or empty link)</span>}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="border border-gray-light rounded-lg p-4 animate-pulse">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-5 w-20 bg-gray-light rounded" />
        <div className="h-5 w-16 bg-gray-light rounded" />
      </div>
      <div className="h-4 w-3/4 bg-gray-light rounded mb-2" />
      <div className="h-3 w-1/2 bg-gray-light rounded" />
    </div>
  );
}

export function BrokenLinks({
  pageResults,
  loading = false,
  selectedPageUrl,
  reviewedLinks,
  onToggleLinkReviewed,
}: BrokenLinksProps) {
  const [groupBy, setGroupBy] = useState<GroupBy>('status');

  // Collect the links the check reported, then split them. A link whose probe never
  // settled (anti-bot 403, rate limit, 5xx, timeout) is not a confirmed break, so it
  // stays out of every count below and is listed on its own for a human to open.
  const { allBrokenLinks, unverifiedLinks } = useMemo(() => {
    const broken: BrokenLink[] = [];
    const unverified: BrokenLink[] = [];
    for (const page of pageResults) {
      if (selectedPageUrl && page.pageUrl !== selectedPageUrl) continue;
      for (const link of page.brokenLinks ?? []) {
        (link.needsManualReview ? unverified : broken).push(link);
      }
    }
    return { allBrokenLinks: broken, unverifiedLinks: unverified };
  }, [pageResults, selectedPageUrl]);

  // Group links by status code
  const linksByStatus = useMemo(() => {
    const groups = new Map<number, BrokenLink[]>();
    for (const link of allBrokenLinks) {
      const existing = groups.get(link.statusCode) || [];
      groups.set(link.statusCode, [...existing, link]);
    }
    return new Map([...groups.entries()].sort((a, b) => a[0] - b[0]));
  }, [allBrokenLinks]);

  // Group links by source page
  const linksBySource = useMemo(() => {
    const groups = new Map<string, BrokenLink[]>();
    for (const link of allBrokenLinks) {
      const existing = groups.get(link.sourceUrl) || [];
      groups.set(link.sourceUrl, [...existing, link]);
    }
    return new Map([...groups.entries()].sort((a, b) => b[1].length - a[1].length));
  }, [allBrokenLinks]);

  // Count by status type
  const statusCounts = useMemo(() => {
    const counts = { notFound: 0, serverError: 0, timeout: 0, other: 0, total: allBrokenLinks.length };
    for (const link of allBrokenLinks) {
      if (link.statusCode === 404) counts.notFound++;
      else if (link.statusCode >= 500) counts.serverError++;
      else if (link.statusCode === 0) counts.timeout++;
      else counts.other++;
    }
    return counts;
  }, [allBrokenLinks]);

  // Count internal vs external
  const locationCounts = useMemo(() => {
    return allBrokenLinks.reduce(
      (acc, link) => {
        if (link.isExternal) acc.external++;
        else acc.internal++;
        return acc;
      },
      { internal: 0, external: 0 }
    );
  }, [allBrokenLinks]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="card">
          <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
            <Link2Off className="text-coral" size={24} />
            Broken Links
          </h3>
          <div className="animate-pulse">
            <div className="h-20 bg-gray-light rounded mb-4" />
            <div className="grid grid-cols-4 gap-4">
              <div className="h-16 bg-gray-light rounded" />
              <div className="h-16 bg-gray-light rounded" />
              <div className="h-16 bg-gray-light rounded" />
              <div className="h-16 bg-gray-light rounded" />
            </div>
          </div>
        </div>
        <div>
          <h3 className="text-heading-3 font-semibold mb-4">All Broken Links</h3>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
          <Link2Off className="text-coral" size={24} />
          Broken Links Summary
        </h3>

        {allBrokenLinks.length > 0 ? (
          <>
            {/* Total count */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertTriangle size={24} className="text-status-error" />
                <span className="text-heading-1 font-bold text-status-error">{statusCounts.total}</span>
                <span className="text-body text-gray-medium">broken links found</span>
              </div>
            </div>

            {/* Breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <div className="text-heading-3 font-bold text-red-800">{statusCounts.notFound}</div>
                <div className="text-body-sm text-gray-medium">404 Not Found</div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div className="text-heading-3 font-bold text-orange-800">{statusCounts.serverError}</div>
                <div className="text-body-sm text-gray-medium">Server Errors (5xx)</div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                <div className="text-heading-3 font-bold text-yellow-800">{statusCounts.timeout}</div>
                <div className="text-body-sm text-gray-medium">Timeouts</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-heading-3 font-bold text-gray-700">{statusCounts.other}</div>
                <div className="text-body-sm text-gray-medium">Other Errors</div>
              </div>
            </div>

            {/* Internal vs External */}
            <div className="mt-4 flex items-center gap-4 text-body-sm">
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-gray-500" />
                <span className="font-medium">{locationCounts.internal}</span>
                <span className="text-gray-medium">Internal</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe size={14} className="text-blue-500" />
                <span className="font-medium">{locationCounts.external}</span>
                <span className="text-gray-medium">External</span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <CheckCircle size={48} className="mx-auto text-status-success opacity-50 mb-4" />
            <h4 className="text-heading-3 font-semibold text-dark-text mb-2">
              No Broken Links Found
            </h4>
            <p className="text-body text-gray-medium">
              All links on the scanned pages are working correctly.
            </p>
          </div>
        )}
      </div>

      {/* Links we could not verify - listed, not counted */}
      <UnverifiedLinksSection
        links={unverifiedLinks}
        subject="links"
        reviewedLinks={reviewedLinks}
        onToggleReviewed={onToggleLinkReviewed}
      />

      {/* Links List */}
      {allBrokenLinks.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-heading-3 font-semibold flex items-center gap-2">
              <AlertTriangle className="text-coral" size={24} />
              All Broken Links
            </h3>

            {/* Group By Selector */}
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-medium" />
              <span className="text-body-sm text-gray-medium">Group by:</span>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-body-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
              >
                <option value="status">Status Code</option>
                <option value="source">Source Page</option>
                <option value="none">No Grouping</option>
              </select>
            </div>
          </div>

          {groupBy === 'status' && (
            <div className="space-y-6">
              {Array.from(linksByStatus.entries()).map(([statusCode, links]) => {
                const config = getStatusConfig(statusCode);
                return (
                  <div key={statusCode}>
                    <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                        {statusCode} {config.label}
                      </span>
                      <span className="text-body-sm font-normal">({links.length} links)</span>
                    </h4>
                    <div className="space-y-3">
                      {links.map((link, idx) => (
                        <BrokenLinkCard key={`${link.targetUrl}-${idx}`} link={link} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {groupBy === 'source' && (
            <div className="space-y-6">
              {Array.from(linksBySource.entries()).map(([sourceUrl, links]) => (
                <div key={sourceUrl}>
                  <h4 className="text-body font-semibold text-gray-medium mb-3 flex items-center gap-2">
                    <FileText size={16} />
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-coral hover:underline"
                    >
                      {truncateUrl(sourceUrl, 50)}
                    </a>
                    <span className="text-body-sm font-normal">({links.length} broken links)</span>
                  </h4>
                  <div className="space-y-3">
                    {links.map((link, idx) => (
                      <BrokenLinkCard key={`${link.targetUrl}-${idx}`} link={link} showSource={false} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {groupBy === 'none' && (
            <div className="space-y-3">
              {allBrokenLinks.map((link, idx) => (
                <BrokenLinkCard key={`${link.targetUrl}-${idx}`} link={link} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
